import { el, clear } from "../components/dom.js";
import { searchBar } from "../components/search-bar.js";
import { expandableResultCard } from "../components/search-result.js";
import { searchComics, getComic, comicsErrorMessage } from "../api/comics.js";
import { searchBooks, getBook } from "../api/books.js";
import { addComic, deleteComicByKey, listComics, setComicCollects } from "../db/comics.js";
import {
	comicKey,
	comicFormatLabel,
	creditLines,
	comicFromMetron,
	comicFromOpenLibrary,
} from "../utils/comics.js";
import { navigateBack, getQueryParams } from "../main.js";
import { magScreenHead, MAG } from "../components/magazine/kit.js";

// Add a comic. Two searches, one screen:
//   • Single issues — Metron (also finds the collected editions Metron knows).
//     Type "X-Men 188" or "X-Men (2004) #188" to jump to an issue. Rows are thin,
//     so the full issue (credits, format, reprints) is fetched on expand / Add —
//     one Metron call per comic, cached for the screen's lifetime.
//   • Collected editions — Open Library (trades / hardcovers / omnibuses). The
//     user fills in what a trade collects on its detail screen ("Collects…").

const MODES = [
	{ value: "metron", label: "Single issues", placeholder: "Series + issue, e.g. X-Men 188…" },
	{ value: "openlibrary", label: "Collected editions", placeholder: "Trade / hardcover title…" },
];
const COLOR = "#1fbf5f";

export async function renderAddComic(root) {
	const head = magScreenHead("Add a Comic", {
		eyebrow: "Find & collect",
		eyebrowColor: COLOR,
		onBack: () => navigateBack("/comics"),
	});

	const resultsContainer = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", padding: "6px 0 24px" } });
	const statusLine = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px",
			letterSpacing: ".14em", textTransform: "uppercase", color: MAG.lime, padding: "10px 0 4px",
		},
	});
	const moreBtn = el("button", {
		type: "button",
		hidden: true,
		onclick: () => loadMore(),
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px", letterSpacing: ".06em",
			textTransform: "uppercase", borderRadius: "999px", padding: "9px 16px", cursor: "pointer",
			color: MAG.ink, background: "rgba(255,255,255,.07)", border: `1px solid ${MAG.line}`,
			alignSelf: "center", margin: "0 0 24px",
		},
	}, "More results");

	const existing = await listComics();
	const addedKeys = new Set(existing.map((c) => c.comic_key));

	// Arriving from a creator's run ("+ Add a collection for this run"): start on
	// Collected editions with the series searched, and give whatever gets added a
	// "Collects…" entry for that run.
	const params = getQueryParams();
	const prefill = params.get("collects_ranges")
		? {
				series_id: params.get("collects_series_id") || null,
				series_label: params.get("collects_label") ?? "",
				ranges: params.get("collects_ranges"),
			}
		: null;
	let mode = params.get("mode") === "openlibrary" ? "openlibrary" : "metron";
	let inflight = null;
	let lastQuery = "";
	let lastResults = []; // normalized rows: { key, source, external_id, title, subtitle, cover_url, raw }
	let nextPage = null;
	const fullByKey = new Map(); // comic_key -> full source record

	async function fullRecord(row) {
		if (fullByKey.has(row.key)) return fullByKey.get(row.key);
		const rec = row.source === "metron" ? await getComic(row.external_id) : await getBook(row.external_id);
		fullByKey.set(row.key, rec);
		return rec;
	}

	function metronRow(r) {
		return {
			key: comicKey("metron", String(r.issue_id)),
			source: "metron",
			external_id: String(r.issue_id),
			title: r.label ?? "Untitled",
			subtitle: r.cover_date ? r.cover_date.slice(0, 4) : "",
			cover_url: r.cover_url,
			raw: r,
		};
	}

	function bookRow(b) {
		return {
			key: comicKey("openlibrary", b.ol_id),
			source: "openlibrary",
			external_id: b.ol_id,
			title: b.title ?? "Untitled",
			subtitle: [(b.authors ?? []).slice(0, 2).join(", "), b.published_year].filter(Boolean).join(" · "),
			cover_url: b.cover_url,
			raw: b,
		};
	}

	function metronDetails(full) {
		const lines = creditLines(full.credits?.map((c) => ({ name: c.name, roles: c.roles })));
		return [
			{ label: "Format", value: comicFormatLabel(full.format) },
			{ label: "Publisher", value: full.publisher ?? "" },
			{ label: "Story", value: (full.story_titles ?? []).slice(0, 2).join("; ") },
			{ label: "Writer", value: lines.writer.join(", ") },
			{ label: "Art", value: lines.art.join(", ") },
			{ label: "Collects", value: full.reprints?.length ? `${full.reprints.length} issues` : "" },
		];
	}

	function renderResults() {
		clear(resultsContainer);
		for (const row of lastResults) {
			resultsContainer.append(
				expandableResultCard({
					cover_url: row.cover_url,
					mediaType: "comic",
					title: row.title,
					subtitle: row.subtitle,
					summary: "",
					details: [],
					loadDetails: async () => {
						try {
							const full = await fullRecord(row);
							if (row.source === "metron") {
								return { summary: full.summary ?? "", details: metronDetails(full) };
							}
							return {
								summary: full.summary ?? "",
								details: [
									{ label: "Writer", value: (row.raw.authors ?? []).join(", ") },
									{ label: "Year", value: row.raw.published_year ? String(row.raw.published_year) : "" },
								],
							};
						} catch (err) {
							return { summary: comicsErrorMessage(err), details: [] };
						}
					},
					added: addedKeys.has(row.key),
					onToggleAdd: (want) => toggle(row, want),
				}),
			);
		}
		moreBtn.hidden = !(mode === "metron" && nextPage);
	}

	// Returns the resulting added-state so the card can repaint in place.
	async function toggle(row, want) {
		try {
			if (!want) {
				await deleteComicByKey(row.key);
				addedKeys.delete(row.key);
				return false;
			}
			const full = await fullRecord(row);
			const rec = await addComic(row.source === "metron" ? comicFromMetron(row, full) : comicFromOpenLibrary(row, full));
			if (prefill) {
				try {
					await setComicCollects(rec.id, [...(rec.collects ?? []), prefill]);
				} catch (err) {
					statusLine.textContent = `Added, but couldn't set what it collects: ${err.message}`;
				}
			}
			addedKeys.add(row.key);
			return true;
		} catch (err) {
			statusLine.textContent = `Could not ${want ? "add" : "remove"}: ${comicsErrorMessage(err)}`;
			throw err;
		}
	}

	async function runSearch(q) {
		if (inflight) inflight.abort();
		lastQuery = q;
		lastResults = [];
		nextPage = null;
		clear(resultsContainer);
		moreBtn.hidden = true;
		statusLine.textContent = "";
		if (!q) return;

		const controller = new AbortController();
		inflight = controller;
		statusLine.textContent = "Searching…";
		try {
			if (mode === "metron") {
				const page = await searchComics(q, { signal: controller.signal });
				if (controller.signal.aborted) return;
				lastResults = page.results.map(metronRow);
				nextPage = page.next_page;
				statusLine.textContent = page.count
					? `${page.count} issue${page.count === 1 ? "" : "s"}`
					: "No issues. Try the series name alone, or “Series 12” for an issue.";
			} else {
				const books = await searchBooks(q, { signal: controller.signal });
				if (controller.signal.aborted) return;
				lastResults = books.filter((b) => b.ol_id).map(bookRow);
				statusLine.textContent = lastResults.length
					? `${lastResults.length} result${lastResults.length === 1 ? "" : "s"}`
					: "No results.";
			}
			renderResults();
		} catch (err) {
			if (err.name === "AbortError") return;
			statusLine.textContent = `Error: ${comicsErrorMessage(err)}`;
		} finally {
			if (inflight === controller) inflight = null;
		}
	}

	async function loadMore() {
		if (!nextPage || mode !== "metron") return;
		moreBtn.disabled = true;
		try {
			const page = await searchComics(lastQuery, { page: nextPage });
			lastResults = lastResults.concat(page.results.map(metronRow));
			nextPage = page.next_page;
			renderResults();
		} catch (err) {
			statusLine.textContent = `Error: ${comicsErrorMessage(err)}`;
		} finally {
			moreBtn.disabled = false;
		}
	}

	// Metron searches cost budget (20/min), so wait a little longer for the typing
	// to settle than the other add screens do.
	const bar = searchBar({
		placeholder: MODES.find((m) => m.value === mode).placeholder,
		delay: 700,
		onInput: (q) => runSearch(q),
	});

	const modeChips = el("div", { style: { display: "flex", gap: "7px", flexWrap: "wrap", padding: "12px 0 0" } });
	function paintChips() {
		for (const c of modeChips.children) {
			const on = c.dataset.value === mode;
			Object.assign(c.style, {
				color: on ? "#0b0b0f" : MAG.dim, background: on ? MAG.lime : "rgba(255,255,255,.07)",
				border: on ? "0" : `1px solid ${MAG.line}`, fontWeight: on ? "900" : "800",
			});
		}
	}
	for (const m of MODES) {
		modeChips.append(
			el("button", {
				type: "button",
				dataset: { value: m.value },
				onclick: () => {
					if (mode === m.value) return;
					mode = m.value;
					bar.input.placeholder = m.placeholder;
					paintChips();
					runSearch(bar.input.value.trim());
				},
				style: {
					fontFamily: "'Archivo', sans-serif", fontSize: "10px", letterSpacing: ".05em",
					textTransform: "uppercase", borderRadius: "999px", padding: "7px 13px", cursor: "pointer",
				},
			}, m.label),
		);
	}
	paintChips();

	root.append(
		el(
			"div.view",
			{},
			head,
			prefill ? prefillBanner(prefill) : null,
			el("div", { style: { padding: "14px 0 0" } }, bar.node),
			modeChips,
			statusLine,
			resultsContainer,
			el("div", { style: { display: "flex", justifyContent: "center" } }, moreBtn),
		),
	);

	// A prefilled search (from a creator's run) runs straight away.
	const initialQuery = (params.get("q") ?? "").trim();
	if (initialQuery) {
		bar.input.value = initialQuery;
		runSearch(initialQuery);
	}
}

function prefillBanner(prefill) {
	return el(
		"div",
		{
			style: {
				margin: "12px 0 0", padding: "10px 12px", borderRadius: "12px",
				background: "rgba(31,191,95,.14)", border: "1px solid rgba(31,191,95,.5)",
				fontFamily: "'Archivo', sans-serif", fontSize: "12px", lineHeight: "1.45", color: MAG.ink,
			},
		},
		"Adding a collection for ",
		el("strong", {}, `${prefill.series_label} #${prefill.ranges}`),
		". Whatever you add here will list those issues under “Collects” — trim them on its page if it holds fewer.",
	);
}
